import { useId, useState, type ReactNode } from 'react';
import { Camera } from 'lucide-react';
import Avatar from './Avatar';
import { usersService, type User } from '../../services/usersService';

interface AvatarEditorProps {
  user: User;
  onChange: (avatarUrl: string | null) => void;
  compact?: boolean;
  children?: ReactNode;
}

const AvatarEditor = ({
  user,
  onChange,
  compact = false,
  children,
}: AvatarEditorProps) => {
  const inputId = useId();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const save = async (file: File | null) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = file
        ? await usersService.uploadAvatar(user.id, file)
        : await usersService.removeAvatar(user.id);
      onChange(result.avatarUrl);
      setMessage(file ? 'Avatar updated' : 'Avatar removed');
      if (compact) setEditing(false);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Could not update the avatar'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? 'w-full space-y-3' : 'space-y-3'}>
      <div className={compact ? 'flex items-center gap-6' : undefined}>
        <div className="relative shrink-0">
          <Avatar
            initials={`${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()}
            src={user.avatarUrl}
            alt={`${user.firstName} ${user.lastName}`}
            className="w-20 h-20 rounded-full"
          />
          {compact && (
            <button
              type="button"
              className="btn btn-circle btn-xs absolute -bottom-1 -right-1 h-7 w-7 border-base-300 bg-base-100"
              aria-label="Change avatar"
              aria-expanded={editing}
              aria-controls={`${inputId}-controls`}
              disabled={busy}
              onClick={() => {
                setEditing((current) => !current);
                setError('');
                setMessage('');
              }}
            >
              <Camera className="h-4 w-4" />
            </button>
          )}
        </div>
        {children}
      </div>
      {(!compact || editing) && (
        <div id={`${inputId}-controls`} className="space-y-3">
          <label htmlFor={inputId} className="block text-sm font-medium">
            Profile image
            <input
              id={inputId}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="file-input file-input-sm mt-2 w-full"
              disabled={busy}
              aria-describedby={`${inputId}-help`}
              onChange={async (event) => {
                const input = event.currentTarget;
                const file = input.files?.[0];
                input.value = '';
                if (file) await save(file);
              }}
            />
          </label>
          <p id={`${inputId}-help`} className="text-sm text-base-content/70">
            JPEG, PNG, or WebP, up to 2 MB. Changes save immediately.
          </p>
          {user.avatarUrl && (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => save(null)}
            >
              Remove avatar
            </button>
          )}
          {compact && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setError('');
                setMessage('');
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}
      {busy && <p role="status">Saving avatar...</p>}
      {message && (
        <p
          role="status"
          className={compact && !editing ? 'sr-only' : undefined}
        >
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default AvatarEditor;
